import { CaretRightOutlined } from '@ant-design/icons'
import { CallReadable, callsListOptions, CallTypeEnum } from '@crm/api'
import { AppLink, AudioPlayer } from '@crm/components'
import { CallsFilter } from '@crm/features/calls/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters, formatSmallDuration } from '@crm/utils'
import {
  ConditionalText,
  DayLabel,
  TableContainer,
  TitleSection
} from '@shared/components'
import { useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { getFullName } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Button, Flex, Tag, Typography } from 'antd'
import * as React from 'react'

import type { TableColumnsType } from 'antd'

export const Calls = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const [callItem, setCallItem] = React.useState<CallReadable>()
  const sorter = useTableSorter()

  const { data: calls, isLoading } = useQuery({
    ...callsListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  })

  const onOpenPlayer = React.useCallback(
    (call: CallReadable): void => {
      if (call.file_legacy_url && call.id !== callItem?.id) {
        setCallItem(call)
      }
    },
    [callItem?.id]
  )

  const onCell = () => ({
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onPointerDown: (e: React.MouseEvent) => e.stopPropagation()
  })

  const columns: TableColumnsType<CallReadable> = React.useMemo(
    () => [
      {
        title: t('calls.full_name'),
        sorter: true,
        key: 'first_name',
        render: ({ contact }) => {
          const fullName = getFullName(contact)

          return fullName ? (
            <AppLink to={`/leads/${contact?.lead}`}>{fullName}</AppLink>
          ) : (
            '-'
          )
        }
      },
      {
        title: t('calls.to_number'),
        sorter: true,
        key: 'to_phone_number',
        onCell,
        render: ({ to_phone_number }) =>
          to_phone_number ? (
            <Typography.Link
              href={`tel:+${to_phone_number}`}
              target="_blank"
              rel="noreferrer"
            >
              +{to_phone_number}
            </Typography.Link>
          ) : (
            '-'
          )
      },
      {
        title: t('calls.call_type'),
        sorter: true,
        key: 'type',
        render: ({ type }) =>
          type ? (
            <Tag color={CallTypeEnum[type as keyof typeof CallTypeEnum]}>
              {t(`calls.${type}`)}
            </Tag>
          ) : (
            '-'
          )
      },
      {
        title: t('calls.duration'),
        sorter: true,
        key: 'call_duration',
        render: ({ call_duration }) => (
          <ConditionalText condition={call_duration}>
            {formatSmallDuration(call_duration)}
          </ConditionalText>
        )
      },
      {
        title: t('calls.answered'),
        sorter: true,
        key: 'answered',
        render: ({ answered }) =>
          answered ? (
            <Tag color={answered ? 'processing' : 'danger'}>
              {answered ? t('calls.answered_true') : t('calls.answered_false')}
            </Tag>
          ) : (
            '-'
          )
      },
      {
        title: t('calls.agent'),
        render: ({ agent }) => {
          const fullName = getFullName(agent)

          return (
            <ConditionalText condition={!!fullName}>{fullName}</ConditionalText>
          )
        }
      },
      {
        title: t('calls.date_time'),
        sorter: true,
        key: 'created_at',
        render: ({ createdAt }) => <DayLabel date={createdAt} />
      },
      {
        width: 30,
        align: 'right',
        render: (item) => (
          <Button
            size="small"
            type={callItem?.id === item?.id ? 'primary' : 'default'}
            disabled={!item?.file_legacy_url}
            onClick={() => onOpenPlayer(item)}
            icon={<CaretRightOutlined />}
          />
        )
      }
    ],
    [callItem?.id, onOpenPlayer, t]
  )

  return (
    <>
      <Flex vertical>
        <TitleSection title={t(`calls.calls`)} isLoading={isLoading} />
      </Flex>

      <TableContainer
        loading={isLoading}
        rowClassName="pointer"
        data={calls}
        columns={columns}
        onChange={(_, __, result) => sorter(result)}
        showSorterTooltip={false}
        leftChildren={<CallsFilter />}
      />

      {callItem && (
        <AudioPlayer record={callItem} onClose={() => setCallItem(undefined)} />
      )}
    </>
  )
}
